import {SimplexClassic as BaseSimplex} from 'services/sports/mlb/simplex/simplex-classic';
import Const from 'constants';
import Action from 'actions';
import RuleBuilder from 'services/simplex/rule-builder';
import {getMapOfLocked} from 'components/organisms/late-swap/helpers';
import {getSimplexUnitName} from 'helpers/simplex-helper';
import {preparePlayerUID} from 'helpers/player-helper';

export default class SimplexClassic extends BaseSimplex {
    max({incomingValues = {}}) {
        return super.max({
            incomingValues: {
                checkIfPlayerOnDoublePosition: true,
            },
        });
    }

    maxRandom({incomingValues = {}}) {
        return super.maxRandom({
            incomingValues: {
                checkIfPlayerOnDoublePosition: true,
            },
        });
    }

    createRulesForPositionOfPlayerThatStaysOnFlex({incomingValues = {}}) {
        const {
            sportConfig,
            lateSwap: {csvLineups},
            playersData: {games, preparedPlayers},
            lineupResolvingProcess,
        } = Action.getState();
        const {unitIdPropName = 'playerId'} = incomingValues;
        const preparedPlayersPool = preparedPlayers.groupedBy.externalId();
        const players = preparedPlayers.groupedBy.allowedPositions();
        const entryId = lineupResolvingProcess.getEntryId();
        const lateswapCsvLineup = csvLineups.getOneByEntryId(entryId);
        const mapOfLocked = getMapOfLocked(
            sportConfig.getConfigProp('positionsOrder', {}),
            lateswapCsvLineup,
            function (player) {
                return games.isPlayerGameLocked(player);
            },
        );
        const flexPositions = sportConfig.getConfigProp('flexPlayerPosition');

        let rules = [];
        Object.values(flexPositions).map((positionKey) => {
            const position = sportConfig.getPositionByKey(positionKey);

            if (isEmpty(position) || !mapOfLocked.positions.hasOwnProperty(position.id)) {
                return;
            }

            const playerExternalId = mapOfLocked.positions[position.id];

            if (!preparedPlayersPool.hasOwnProperty(playerExternalId)) {
                return;
            }

            const player = preparedPlayersPool[playerExternalId];

            player.position.map((positionKey) => {
                const actPositionKey = sportConfig.getJointPosition(positionKey) || positionKey;
                const position = sportConfig.getPositionByKey(actPositionKey);

                const rulesForPosition = createRulesForPosition(position, player);

                if (!isEmpty(rulesForPosition)) {
                    rules = [...rules, ...rulesForPosition];
                }
            });
        });

        function createRulesForPosition(position, excludablePlayer) {
            if (isEmpty(position)) {
                return [];
            }

            const {allowedPositions} = position;
            const simplexUnitNamesRow = {};
            const addedPlayers = [];

            players.map((player) => {
                if (
                    excludablePlayer.getId() === player.getId() ||
                    !inArray(player.position, allowedPositions) ||
                    inArray(player[unitIdPropName], addedPlayers)
                ) {
                    return;
                }

                simplexUnitNamesRow[getSimplexUnitName(player[unitIdPropName])] = 1;

                addedPlayers.push(player[unitIdPropName]);
            });

            if (isEmpty(simplexUnitNamesRow)) {
                return [];
            }

            const rules = [];

            const atLeastRuleBuilder = RuleBuilder.fromAtLeast(position.minPlayers);
            atLeastRuleBuilder.addVars({...simplexUnitNamesRow});
            rules.push(atLeastRuleBuilder.createRule());

            const atMostRuleBuilder = RuleBuilder.fromAtMost(position.maxPlayers);
            atMostRuleBuilder.addVars({...simplexUnitNamesRow});
            rules.push(atMostRuleBuilder.createRule());

            return rules;
        }

        return [...rules];
    }

    subjectToPermanent() {
        const constraints = {
            player: this.exec(
                'player',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            salary: this.exec(
                'salary',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            projectedOwnershipCap: this.exec(
                'projectedOwnership',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            projectedOwnershipForce: this.exec(
                'forceOfProjectedOwnership',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            positions: this.exec(
                'positions',
                {
                    unitIdPropName: 'uniqueId',
                },
                [],
            ),
            teams: this.exec(
                'team',
                {
                    unitIdPropName: 'uniqueId',
                },
                [],
            ),
            limitPositions: this.exec(
                'limitPositions',
                {
                    unitIdPropName: 'uniqueId',
                },
                [],
            ),
            minTeams: this.exec(
                'minTeams',
                {
                    unitIdPropName: 'uniqueId',
                },
                [],
            ),
            projectionBoostRule: this.exec(
                'projectionBoostRule',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            bound: this.exec(
                'bound',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
        };

        return {...constraints};
    }

    subjectToAlterable() {
        const constraints = {
            playersWhoseGamesAreLocked: this.exec(
                'playersWhoseGamesAreLocked',
                {
                    checkIfPlayerOnDoublePosition: true,
                    unitIdPropName: 'uniqueId',
                },
                [],
            ),
            createdLineups: this.exec(
                'createdLineups',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            multiPositions: this.exec('multiPositions', {}, []),
            hitterStacks: this.exec(
                'stackHitters',
                {
                    getUnitIdsByPlayer: (player) => preparePlayerUID(player),
                },
                [],
            ),
            nonControlledHittersStacking: this.exec(
                'nonControlledHittersStacking',
                {
                    getUnitIdsByPlayer: (player) => preparePlayerUID(player),
                },
                [],
            ),
            exposureMax: this.exec(
                'exposureMax',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            exposureMin: this.exec(
                'exposureMin',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
            teamExposureMax: this.exec(
                'teamExposureMax',
                {
                    minPlayersQuantityToIdentifyUsedTeam: Const.MLB_MIN_PLAYERS_QUANTITY_TO_IDENTIFY_USED_TEAM,
                },
                [],
            ),
            playerCombinationStacking: this.exec(
                'playerCombinationStacking',
                {
                    checkIfPlayerOnDoublePosition: true,
                },
                [],
            ),
        };

        return {...constraints};
    }
}
